import { useEffect, useState } from "react";
import styles from "./list.module.css";
import dayjs from "dayjs";
import { Button, Snackbar, Alert, Dialog, DialogTitle, IconButton, DialogContent, Select, FormControl, InputLabel, MenuItem, OutlinedInput, Box, Chip, Checkbox, ListItemText } from "@mui/material";
import { List, Map } from "react-feather";
import { Close } from "@mui/icons-material";
import axios from "axios";
import Loader from "../../../components/Loader";
import { authData, withAdminAuth } from "../../../components/GetAuth";
import Step1 from "./steps/Step1";
import Step2 from "./steps/Step2";
import Step3 from "./steps/Step3";
import ListView from "./ListView";
import PageHeader from "../../../components/PageHeader";
import BookingSkeleton from "./BookingSkeleton";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import AddDriverForm from "../../../components/popups/AddDriverForm";
import AssignBookingDetailsToDriver from "../../../components/popups/AssignBookingDetailsToDriver";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

const BookingList = () => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [filterVars, setFilterVars] = useState({ searchKey: "", date: null });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [formOpen, setFormOpen] = useState(false);
  const [driverFormOpen, setDriverFormOpen] = useState(false);
  const [driverList, setDriverList] = useState([]);
  const [selectedDriverList, setSelectedDriverList] = useState([]);
  const [selected, setSelected] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [routes, setRoutes] = useState([]);
  const [viewType, setViewType] = useState('map');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const { websiteData } = useWebsite();
  useSiteBranding("Assign To Driver", websiteData);

  useEffect(() => {
    fetchBookings();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (page === 0) {
      fetchBookings();
    } else {
      setPage(0);
    }
  }, [filterVars]);

  const fetchBookings = () => {
    axios.get(process.env.REACT_APP_API_URL + "v1/assign-bookings", {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      },
      params: {
        page: page + 1, // Laravel uses 1-based index
        per_page: rowsPerPage,
        search: filterVars.searchKey,
        start_date: filterVars.date ? dayjs(filterVars.date[0]).format("YYYY-MM-DD") : null,
        end_date: filterVars.date ? dayjs(filterVars.date[1]).format("YYYY-MM-DD") : null,
      }
    }).then((response) => {
      setContentLoading(false);

      setFilteredData(response.data.data.data); // actual rows
      setTotalCount(response.data.data.total); // total rows
    }).catch(() => {
      setContentLoading(false);
    });
  }

  const selectRowEvent = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  }

  useEffect(() => {
    axios.get(process.env.REACT_APP_API_URL + "v1/p-driver-dd-list", {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      }
    }).then((response) => {
      if (response.data.status === "SUCCESS") {
        setDriverList(response.data.data);
      }
    });
  }, [API_TOKEN]);

  const getOptimizeRoute = () => {
    setLoading(true);

    let bookingListTemp = filteredData?.filter(i => selected?.indexOf(i?.id) > -1)?.map(i => {
      return { id: i?.id, booking_id: i?.booking_id, customer_name: i?.customer_name, address: i?.pickup_details?.address, lat: i?.pickup_details?.latitude, lng: i?.pickup_details?.longitude };
    });
    let driverListTemp = driverList?.filter(i => selectedDriverList?.indexOf(i?.id) > -1)?.map(i => {
      return { id: i?.id, name: i?.name, address: i?.address, lat: i?.latitude, lng: i?.longitude };
    });

    setLoading(true);

    axios.post(process.env.REACT_APP_API_URL + 'v1/optimize-route', { drivers: driverListTemp, pickups: bookingListTemp }, {
      headers: {
        Authorization: `Bearer ${auth?.api_token}`,
      }
    }).then(response => {
      setLoading(false);
      setRoutes(response.data.data);
      setCurrentStep(2);
    }).catch(error => {
      setLoading(false);
    });
  }

  const onAssign = () => {
    setSnackbarType("success");
    setSnackbarOpen(true);
    setSnackbarMessage("Driver assigned successfully.");

    setSelected([]);
    setLoading(false);

    if (page === 0) {
      fetchBookings();
    } else {
      setPage(0);
    }
  };


  return (<>
    <Loader open={loading} setOpen={setLoading.bind(this)} />
    <div className={`${styles.MainDiv}`}>
      <PageHeader title="Booking" trail={[{ label: 'Assign To Driver' }]} />
      <div className={`${styles.MainCard}`}>
        {contentLoading && <BookingSkeleton />}

        {/* Labeled segmented control instead of bare icon buttons — on a
            dispatch console the two views mean very different things
            (a live map vs. a scannable manifest), so the choice deserves
            a word, not just a glyph. */}
        {!contentLoading && <div className={`${styles.ViewSwitchWrap}`} role="group" aria-label="Choose view">
          <Button
            aria-label="Map view"
            aria-pressed={viewType === 'map'}
            className={viewType === 'map' ? `${styles.SwitchBtn} ${styles.SwitchBtnActive}` : `${styles.SwitchBtn}`}
            onClick={(e) => setViewType('map')}
          >
            <Map size={16} /> Map
          </Button>
          <Button
            aria-label="List view"
            aria-pressed={viewType === 'list'}
            className={viewType === 'list' ? `${styles.SwitchBtn} ${styles.SwitchBtnActive}` : `${styles.SwitchBtn}`}
            onClick={(e) => setViewType('list')}
          >
            <List size={16} /> List
          </Button>
        </div>}

        {!contentLoading && (<>
          {viewType === 'map' && <>
            {currentStep === 1 && <Step1 selected={selected} filteredData={filteredData} filterVars={filterVars} driverList={driverList} selectedDriverList={selectedDriverList} setFilterVars={setFilterVars.bind(this)} setFormOpen={setFormOpen.bind(this)} setSelectedDriverList={setSelectedDriverList.bind(this)} getOptimizeRoute={getOptimizeRoute.bind(this)} selectRowEvent={selectRowEvent.bind(this)} setSelected={setSelected.bind(this)} page={page} setPage={setPage.bind(this)} rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage.bind(this)} totalCount={totalCount} />}

            {currentStep === 2 && <Step2 routes={routes} setCurrentStep={setCurrentStep.bind(this)} />}

            {currentStep === 3 && <Step3 routes={routes} setCurrentStep={setCurrentStep.bind(this)} setLoading={setLoading.bind(this)} setSnackbarType={setSnackbarType.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setRoutes={setRoutes.bind(this)} />}
          </>}
          {viewType === 'list' && <>
            <ListView selected={selected} filteredData={filteredData} filterVars={filterVars} driverList={driverList} setSelected={setSelected.bind(this)} setFilterVars={setFilterVars.bind(this)} setAllData={setFilteredData.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarType={setSnackbarType.bind(this)} page={page} setPage={setPage.bind(this)} rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage.bind(this)} totalCount={totalCount} onAssign={onAssign.bind(this)} />
          </>}

        </>)}
      </div>
    </div>

    <Dialog open={formOpen} onClose={(e) => setFormOpen(false)} fullWidth maxWidth="sm">
      <AssignBookingDetailsToDriver setFormOpen={setFormOpen.bind(this)} driverList={driverList} selectedDriverList={selectedDriverList} setSelectedDriverList={setSelectedDriverList.bind(this)} />
    </Dialog>

    <Dialog open={driverFormOpen} onClose={(e) => setFormOpen(false)} fullWidth maxWidth="sm">
      <AddDriverForm setFormOpen={setDriverFormOpen.bind(this)} setAllData={null} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setSnackbarType={setSnackbarType.bind(this)} setLoading={setLoading.bind(this)} editData={null} />
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={(e) => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert
        onClose={(e) => setSnackbarOpen(false)}
        severity={snackbarType}
        variant="filled"
        sx={{ width: "100%" }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
  </>);
};

export default withAdminAuth(BookingList);